// Printable per-role quick-reference cards (docs/quick-reference/*.md), bundled
// at BUILD time via Vite's `?raw` suffix — same mechanism as content.ts's three
// full guides (see that file's header comment for the mechanics). Kept as a
// separate bundle/module from content.ts deliberately: these are a different
// corpus (five terse one-pagers, not the three narrative guides) with their
// own rendering surface (QuickReference.tsx), not part of the HELP_MAP-driven
// contextual-drawer system at all — so there is no reason to intertwine the
// two module-scope parse tables.
import gatehouseRaw from '../../../../../docs/quick-reference/gatehouse-weighbridge.md?raw';
import productionRaw from '../../../../../docs/quick-reference/production-operator.md?raw';
import storesRaw from '../../../../../docs/quick-reference/stores.md?raw';
import salesAccountsRaw from '../../../../../docs/quick-reference/sales-accounts.md?raw';
import adminRaw from '../../../../../docs/quick-reference/admin.md?raw';
import { parseDoc, type ParsedDoc } from './sections';

export type QuickReferenceDocId = 'gatehouse' | 'production' | 'stores' | 'salesAccounts' | 'admin';

export interface QuickReferenceDoc {
  id: QuickReferenceDocId;
  /** Short role label used as the card's tab/heading chrome outside the
   * markdown itself (the markdown's own h1 repeats a fuller title — this is
   * for compact UI like a card-picker, not currently used but kept for
   * parity with content.ts's HelpDoc.title). */
  title: string;
  content: string;
}

/** The five role cards (docs/quick-reference/*.md — index.md is a plain repo
 * README for that folder, not a card, so it is deliberately not bundled
 * here). */
export const QUICK_REFERENCE_DOCS: Record<QuickReferenceDocId, QuickReferenceDoc> = {
  gatehouse: { id: 'gatehouse', title: 'Gatehouse & Weighbridge', content: gatehouseRaw },
  production: { id: 'production', title: 'Production Operator', content: productionRaw },
  stores: { id: 'stores', title: 'Stores', content: storesRaw },
  salesAccounts: { id: 'salesAccounts', title: 'Sales & Accounts', content: salesAccountsRaw },
  admin: { id: 'admin', title: 'Admin', content: adminRaw },
};

/** Each card parsed exactly ONCE at module scope — same rationale as
 * content.ts's PARSED_DOCS: a pure, one-off computation at bundle-eval time
 * so Markdown never re-parses on render. Headings aren't used for anchor
 * navigation here (no HELP_MAP entry ever points into a quick-reference
 * card), only to satisfy Markdown's `headings` prop so its heading ids stay
 * on the single slug-computation path the rest of the feature uses. */
export const PARSED_QUICK_REFERENCE_DOCS: Record<QuickReferenceDocId, ParsedDoc> = {
  gatehouse: parseDoc(gatehouseRaw),
  production: parseDoc(productionRaw),
  stores: parseDoc(storesRaw),
  salesAccounts: parseDoc(salesAccountsRaw),
  admin: parseDoc(adminRaw),
};

/** Display order in the Quick reference view — mirrors the role list
 * USER_GUIDE.md §1 names them in ("gatehouse and weighbridge staff, plant
 * operators, stores staff, sales/accounts staff, and the site admin"). */
export const QUICK_REFERENCE_DOC_ORDER: QuickReferenceDocId[] = [
  'gatehouse',
  'production',
  'stores',
  'salesAccounts',
  'admin',
];
